import { ReactElement } from 'react';

// Responsive rendering for Contentful-hosted images. The build-time pipeline
// (scripts/generateImages.ts) deliberately skips absolute URLs, so Contentful
// assets are resized and converted at request time instead, via Contentful's
// Image API query params — cached at their edge after the first request.
// Anything that isn't a Contentful asset URL falls through to a plain <img>.

const WIDTHS = [640, 960, 1280, 1920];
const AVIF_QUALITY = 60;
const WEBP_QUALITY = 75;
const JPEG_QUALITY = 80;
const OG_WIDTH = 1200;

export const isContentfulAsset = (src: string) =>
  src.includes('ctfassets.net');

export const contentfulImageUrl = ({
  src,
  width,
  format,
  quality,
}: {
  src: string;
  width: number;
  format: 'avif' | 'jpg' | 'webp';
  quality: number;
}) =>
  `${src}${src.includes('?') ? '&' : '?'}fm=${format}&w=${width}&q=${quality}`;

const srcSetFor = ({
  src,
  format,
  quality,
}: {
  src: string;
  format: 'avif' | 'webp';
  quality: number;
}) =>
  WIDTHS.map(
    (w) => `${contentfulImageUrl({ src, width: w, format, quality })} ${w}w`
  ).join(', ');

// og:image / twitter:image variant — JPEG, since link scrapers (LinkedIn in
// particular) don't reliably support WebP or AVIF.
export const contentfulOgImageUrl = (src: string) =>
  isContentfulAsset(src)
    ? contentfulImageUrl({
        src,
        width: OG_WIDTH,
        format: 'jpg',
        quality: JPEG_QUALITY,
      })
    : src;

interface Props {
  src: string;
  alt: string;
  sizes?: string | undefined;
  className?: string | undefined;
  fetchPriority?: 'auto' | 'high' | 'low' | undefined;
  loading?: 'eager' | 'lazy' | undefined;
  width?: number | undefined;
  height?: number | undefined;
}

export const ContentfulImage = ({
  src,
  alt,
  sizes = '100vw',
  className,
  fetchPriority,
  loading,
  width,
  height,
}: Props): ReactElement => {
  const optionalProps = {
    ...(className !== undefined ? { className } : {}),
    ...(fetchPriority !== undefined ? { fetchPriority } : {}),
    ...(loading !== undefined ? { loading } : {}),
    ...(width !== undefined ? { width } : {}),
    ...(height !== undefined ? { height } : {}),
  };

  if (!isContentfulAsset(src)) {
    return <img src={src} alt={alt} {...optionalProps} />;
  }

  return (
    // display:contents keeps the <img> participating in layout exactly as it
    // would unwrapped (see ResponsiveImage for the positioning gotcha).
    <picture className="contents">
      <source
        type="image/avif"
        srcSet={srcSetFor({ src, format: 'avif', quality: AVIF_QUALITY })}
        sizes={sizes}
      />
      <img
        src={contentfulImageUrl({
          src,
          width: 960,
          format: 'webp',
          quality: WEBP_QUALITY,
        })}
        srcSet={srcSetFor({ src, format: 'webp', quality: WEBP_QUALITY })}
        sizes={sizes}
        alt={alt}
        decoding="async"
        {...optionalProps}
      />
    </picture>
  );
};
